import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Building2,
  Loader2,
  Plus,
  Search,
  Trash2,
  Save,
  FolderPlus,
  ExternalLink,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import client from "@/api/client";
import { BulkToolbar, useBulkSelection } from "@/components/admin/BulkToolbar";
import { CompanyProgramPanel } from "@/components/admin/CompanyProgramPanel";
import { AdminCompanyMembersPanel } from "@/components/admin/AdminCompanyMembersPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

// -----------------------------------------------------------------------------
// Root: list ⇄ detail
// -----------------------------------------------------------------------------
export function AdminCompaniesManager({ onOpenDataRoom, initialCompanyId } = {}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Drill-through filters from the Overview stat tiles.
  const overdueFilter = searchParams.get("overdue") === "1";
  const enrolledFilter = searchParams.get("enrolled") === "1";
  const graduatedFilter = searchParams.get("graduated") === "1";
  const filterBadge = overdueFilter ? "Overdue milestones" : graduatedFilter ? "Graduated" : enrolledFilter ? "Enrolled" : "";

  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(initialCompanyId || null); // company_id
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ per_page: "100" });
      if (q) params.set("q", q);
      if (overdueFilter) params.set("overdue", "1");
      if (enrolledFilter) params.set("enrolled", "1");
      if (graduatedFilter) params.set("graduated", "1");
      const res = await client.get(`/admin/companies?${params}`);
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [q, t, overdueFilter, enrolledFilter, graduatedFilter]);

  const clearFilters = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("overdue"); next.delete("enrolled"); next.delete("graduated");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => { load(); }, [load]);
  // Deep link: parent tells us which company to open on first mount.
  useEffect(() => {
    if (initialCompanyId) setSelected(initialCompanyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCompanyId]);

  const bulk = useBulkSelection("companies", (it) => it.company_id, load);
  const allSelected = items.length > 0 && items.every((c) => bulk.isSelected(c.company_id));
  const bulkActions = [
    {
      key: "delete",
      label: t("admin.delete"),
      icon: <Trash2 className="h-3.5 w-3.5" />,
      variant: "destructive",
      requiresConfirm: true,
      confirmTitle: t("adminCompanies.bulkDeleteTitle"),
      confirmBody: t("adminCompanies.bulkDeleteBody"),
    },
  ];

  const create = async (e) => {
    e?.preventDefault?.();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await client.post("/admin/companies", { name });
      setNewName("");
      toast.success(t("adminCompanies.created"));
      setSelected(res.data.company_id);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setCreating(false);
    }
  };

  if (selected) {
    return <CompanyDetail companyId={selected} onBack={() => setSelected(null)} onOpenDataRoom={onOpenDataRoom} />;
  }

  // Rules of hooks: keep useBulkSelection at the top level, above any
  // conditional early return. We declare it before the branching UI so
  // both the list and (hypothetical) detail branches share the same
  // hook order every render.


  return (
    <div className="space-y-6" data-testid="admin-panel-companies">
      <Card className="rounded-2xl border border-border p-6">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Building2 className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-xl font-semibold">{t("adminCompanies.title")}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t("adminCompanies.description")}</p>
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl border border-border p-4">
        <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
          <div className="relative">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("common.search")}
              className="rounded-xl pl-9"
              data-testid="admin-companies-search"
            />
          </div>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t("adminCompanies.newName")}
            className="rounded-xl"
            data-testid="admin-companies-new-name"
          />
          <Button
            type="submit"
            className="btn-brand rounded-xl gap-2"
            disabled={creating || !newName.trim()}
            data-testid="admin-companies-create"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {t("adminCompanies.create")}
          </Button>
        </form>
      </Card>

      {filterBadge && (
        <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2" data-testid="admin-companies-filter-banner">
          <span className="text-xs text-muted-foreground">Filtered by:</span>
          <span className="text-xs font-medium">{filterBadge}</span>
          <span className="text-[11px] text-muted-foreground ml-2">{items.length} match{items.length === 1 ? "" : "es"}</span>
          <button
            type="button"
            className="ml-auto text-[11px] text-primary hover:underline"
            onClick={clearFilters}
            data-testid="admin-companies-filter-clear"
          >
            Clear filter
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-14">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="admin-companies-empty">
          <Building2 className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            {filterBadge ? `No companies match “${filterBadge}”. ` : t("adminCompanies.empty")}
            {filterBadge && (
              <button type="button" onClick={clearFilters} className="text-primary hover:underline">Clear filter</button>
            )}
          </p>
        </Card>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => bulk.toggleAll(items)}
                aria-label={t("common.selectAll")}
                data-testid="admin-companies-select-all"
              />
              {t("common.selectAll")}
            </label>
          </div>
          <BulkToolbar {...bulk} actions={bulkActions} testId="admin-companies-bulk" onClear={bulk.clear} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((c) => {
              const checked = bulk.isSelected(c.company_id);
              return (
                <Card
                  key={c.company_id}
                  className={`rounded-2xl border p-5 hover:border-primary/40 hover:shadow-md transition-all ${checked ? "border-primary/70 bg-primary/5" : "border-border"}`}
                  data-testid={`admin-companies-card-${c.company_id}`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => { e.stopPropagation(); bulk.toggle(c.company_id); }}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1"
                      aria-label={`Select ${c.name}`}
                      data-testid={`admin-companies-select-${c.company_id}`}
                    />
                    <button
                      type="button"
                      onClick={() => setSelected(c.company_id)}
                      className="flex items-start gap-3 min-w-0 text-left flex-1"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary flex-shrink-0">
                        <Building2 className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-display font-semibold truncate">{c.name}</p>
                        {(c.industry || c.region) && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {[c.industry, c.region].filter(Boolean).join(" · ")}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span>{c.room_count || 0} {t("adminCompanies.roomsShort")}</span>
                          <span>·</span>
                          <span>{fmtDate(c.updated_at)}</span>
                        </div>
                      </div>
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Detail: fields + linked rooms + one-click create room
// -----------------------------------------------------------------------------
function CompanyDetail({ companyId, onBack, onOpenDataRoom }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creatingRoom, setCreatingRoom] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await client.get(`/admin/companies/${companyId}`);
      setData(res.data);
      setForm(res.data.company);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  }, [companyId, t]);

  useEffect(() => { load(); }, [load]);

  if (!data || !form) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await client.patch(`/admin/companies/${companyId}`, {
        name: form.name,
        website: form.website,
        industry: form.industry,
        region: form.region,
        stage: form.stage,
        logo_url: form.logo_url,
        pitch_doc_url: form.pitch_doc_url,
        linkedin_url: form.linkedin_url,
        description: form.description,
      });
      setForm(res.data);
      toast.success(t("adminCompanies.infoSaved"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    try {
      await client.delete(`/admin/companies/${companyId}`);
      toast.success(t("adminCompanies.deleted"));
      onBack();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const createRoom = async () => {
    setCreatingRoom(true);
    try {
      const res = await client.post(`/admin/companies/${companyId}/data-rooms`, {});
      toast.success(t("dataRoom.created"));
      if (onOpenDataRoom) onOpenDataRoom(res.data.room_id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setCreatingRoom(false);
    }
  };

  const fields = [
    { key: "name", label: "Name" },
    { key: "website", label: "Website" },
    { key: "industry", label: "Industry" },
    { key: "region", label: "Region" },
    { key: "stage", label: "Stage" },
    { key: "logo_url", label: "Logo URL" },
    { key: "pitch_doc_url", label: "Pitch doc URL" },
    { key: "linkedin_url", label: "LinkedIn URL" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-companies-detail">
      <Card className="rounded-2xl border border-border p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <Button variant="ghost" size="icon" onClick={onBack} className="h-9 w-9 rounded-lg" aria-label={t("adminCompanies.back")} data-testid="admin-companies-back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h2 className="font-display text-xl font-semibold truncate" data-testid="admin-companies-title">{form.name}</h2>
              <p className="text-xs text-muted-foreground mt-1 font-mono">{form.slug}</p>
              {form.source_profile_id && (
                <span className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-widest rounded-full border border-primary/30 bg-primary/10 text-primary px-2 py-0.5">
                  {t("adminCompanies.createdSourceProfile")}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button className="btn-brand rounded-xl gap-2" onClick={createRoom} disabled={creatingRoom} data-testid="admin-companies-create-room">
              {creatingRoom ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderPlus className="h-4 w-4" />}
              {t("adminCompanies.createRoomHere")}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-xl text-destructive hover:text-destructive gap-1.5" data-testid="admin-companies-delete">
                  <Trash2 className="h-4 w-4" /> {t("adminCompanies.deleteCompany")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("adminCompanies.deleteCompany")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("adminCompanies.confirmDelete")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={del} data-testid="admin-companies-delete-confirm">
                    {t("admin.delete")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="info">
        <TabsList className="rounded-xl">
          <TabsTrigger value="info" className="rounded-lg" data-testid="admin-company-tab-info">Info</TabsTrigger>
          <TabsTrigger value="members" className="rounded-lg" data-testid="admin-company-tab-members">Members</TabsTrigger>
          <TabsTrigger value="program" className="rounded-lg" data-testid="admin-company-tab-program">Program</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-6 space-y-6">
          <Card className="rounded-2xl border border-border p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fields.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs">{f.label}</Label>
                  <Input value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="rounded-xl mt-1" data-testid={`admin-companies-field-${f.key}`} />
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <textarea
                value={form.description || ""}
                onChange={(e) => set("description", e.target.value)}
                rows={3}
                className="mt-1 w-full rounded-xl border border-input bg-transparent p-3 text-sm"
                data-testid="admin-companies-field-description"
              />
            </div>
            <div className="flex justify-end">
              <Button className="btn-brand rounded-xl gap-2" onClick={save} disabled={saving} data-testid="admin-companies-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {t("adminCompanies.saveInfo")}
              </Button>
            </div>
          </Card>

          <Card className="rounded-2xl border border-border p-6 space-y-3">
            <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              {t("adminCompanies.linkedRooms")}
            </h3>
            {data.rooms.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4" data-testid="admin-companies-rooms-empty">{t("adminCompanies.linkedRoomsEmpty")}</p>
            ) : (
              <div className="divide-y divide-border">
                {data.rooms.map((r) => (
                  <div key={r.room_id} className="flex items-center gap-3 py-3" data-testid={`admin-companies-room-${r.room_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{r.company_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {r.stage || t("dataRoom.unstaged")} · {r.document_count} {t("dataRoom.docsShort")} · {r.member_count} {t("dataRoom.membersShort")}
                      </p>
                    </div>
                    {onOpenDataRoom && (
                      <Button variant="ghost" size="sm" className="rounded-lg gap-1" onClick={() => onOpenDataRoom(r.room_id)} data-testid={`admin-companies-open-room-${r.room_id}`}>
                        <ExternalLink className="h-4 w-4" /> Open
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="members" className="mt-6">
          <AdminCompanyMembersPanel companyId={companyId} />
        </TabsContent>

        <TabsContent value="program" className="mt-6">
          <CompanyProgramPanel companyId={companyId} onChanged={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default AdminCompaniesManager;
